import type { Appointment, SeriesConflict } from '@salonomy/types';
import { Pill } from '@salonomy/ui';
import { isoDateInZone, longDate, spanLabel } from '../../lib/time.js';

/**
 * WS7 phase 3 — the conflict-aware result of creating / editing a series. Renders BOTH the
 * occurrences that were booked AND the ones that could not be fitted, so the salon sees exactly which
 * standing slots couldn't be honoured (the member is off, the salon is shut, or the chair is already
 * taken). Never a silent drop — the briefs are explicit about this.
 *
 * The reason copy is plain-English (honesty brand): "unavailable" = closed/off/outside hours;
 * "overlap" = an existing booking already holds that chair.
 */

const REASON_COPY: Record<SeriesConflict['reason'], string> = {
  unavailable: 'closed, off, or outside working hours',
  overlap: 'another booking already holds that slot',
};

export function SeriesResultPanel({
  created,
  conflicts,
  tz,
  createdNoun = 'booked',
}: {
  created: Appointment[];
  conflicts: SeriesConflict[];
  tz: string;
  /** "booked" on create, "re-booked" on an apply-forward edit. */
  createdNoun?: string;
}) {
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
        <Pill tone="success">
          {created.length} {createdNoun}
        </Pill>
        {conflicts.length > 0 ? (
          <Pill tone="danger">{conflicts.length} couldn’t fit</Pill>
        ) : (
          <Pill tone="soft">every date fitted</Pill>
        )}
      </div>

      {created.length > 0 ? (
        <div className="settings-list">
          {created.map((a) => (
            <div key={a.id} className="settings-row">
              <div style={{ fontWeight: 600 }}>
                {longDate(isoDateInZone(new Date(a.startAt), tz), tz)}
              </div>
              <span className="muted mono" style={{ fontSize: 12 }}>
                {spanLabel(a.startAt, a.endAt, tz)}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {conflicts.length > 0 ? (
        <div className="conflict-list">
          <div className="conflict-list__head">
            {conflicts.length} {conflicts.length === 1 ? 'date' : 'dates'} couldn’t be booked —
            handle these deliberately (rebook another time or skip).
          </div>
          {conflicts.map((c) => (
            <div key={`${c.occurrenceIndex}-${c.startAt}`} className="conflict-list__row">
              <div>
                <span style={{ fontWeight: 600 }}>
                  {longDate(isoDateInZone(new Date(c.startAt), tz), tz)}
                </span>
                <div className="muted" style={{ fontSize: 12 }}>
                  {REASON_COPY[c.reason]}
                </div>
              </div>
              <span className="muted mono" style={{ fontSize: 12 }}>
                {spanLabel(c.startAt, c.endAt, tz)}
              </span>
            </div>
          ))}
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            The rest of the series is booked — these dates are simply not held.
          </div>
        </div>
      ) : null}
    </div>
  );
}
